import {
  Badge,
  FormField,
  NonCancelableCustomEvent,
  Textarea,
  TextareaProps,
} from '@amzn/awsui-components-core';
import { UserComment, UserCommentState } from '@amzn/awscat-pattern-library-service-model';
import React, { useState } from 'react';
import { MplServiceClientFactory } from '../../api/MplServiceClientFactory';
import constants from '../../common/constants';
import { withAppContext } from '../../contexts/AppContext';
import AppContextProps from '../../contexts/AppContextProps';
import '../../resources/styles/scss/components/comment/CommentBox.css';
import ModalDialogButtonV3 from '../common/ModalDialogButtonV3';
import RenderMultilineText from '../common/RenderMultilineText';

interface Props extends AppContextProps {
  userComment: UserComment;
  onCommentDeleted?: (commentId: string) => Promise<void>;
  onCommentUpdated?: (
    commentId: string,
    updatedAttributes: { state?: UserCommentState; commentData?: string },
  ) => Promise<void>;
  resolveEnabled?: boolean;
  disabled?: boolean;
}

const CommentItem = (props: Props) => {
  const { userComment, appContext, resolveEnabled, onCommentUpdated, onCommentDeleted, disabled } =
    props;
  const { commentId, commentData } = userComment;
  const [updatedCommentData, setUpdatedCommentData] = useState(commentData);
  const [validationErrorMessage, setValidationErrorMessage] = useState('');
  const mplServiceClient = MplServiceClientFactory.create();

  const resolveComment = async () => {
    return updateComment({ state: UserCommentState.RESOLVED });
  };

  const unresolveComment = async () => {
    return updateComment({ state: UserCommentState.PENDING });
  };

  const updateCommentData = async () => {
    return updateComment({ commentData: updatedCommentData });
  };

  const updateComment = async (updatedAttributes: {
    state?: UserCommentState;
    commentData?: string;
  }) => {
    await mplServiceClient.updateComment(commentId, updatedAttributes);
    if (onCommentUpdated) {
      onCommentUpdated(commentId, updatedAttributes);
    }
  };

  const onEditComment = (event: NonCancelableCustomEvent<TextareaProps.ChangeDetail>) => {
    const value = event.detail.value;
    if (!value) {
      setValidationErrorMessage(constants.COMMENTS_EMPTY_ERROR);
    } else if (value.length >= constants.COMMENTS_LENGTH_LIMIT) {
      setValidationErrorMessage(constants.COMMENTS_LENGTH_LIMIT_ERROR);
    } else {
      setValidationErrorMessage('');
    }
    setUpdatedCommentData(value);
  };

  const deleteComment = async () => {
    await mplServiceClient.deleteComment(commentId);
    if (onCommentDeleted) {
      onCommentDeleted(commentId);
    }
  };

  const isCommentOwner = (comment: UserComment) => {
    return comment.userProfile.userId === appContext.authnInfo?.userProfile.userId;
  };

  const renderUpdateForm = () => (
    <FormField
      errorText={validationErrorMessage}
      stretch
      i18nStrings={{ errorIconAriaLabel: 'Error' }}
    >
      <Textarea
        onChange={onEditComment}
        invalid={!!validationErrorMessage}
        value={updatedCommentData}
        ariaRequired
      />
    </FormField>
  );

  const isCommentResolved = () => {
    return userComment && userComment.state === UserCommentState.RESOLVED;
  };

  const commentBoxClassName = isCommentOwner(userComment)
    ? 'comment-author-box'
    : 'comment-non-author-box';

  if (isCommentResolved()) {
    return (
      <div>
        <div className={`comment-resoved-box ${commentBoxClassName}`}>
          <RenderMultilineText text={commentData} />
          <Badge color="blue">Resolved</Badge>
        </div>
        <span className="display-flex">
          <span className="comment-action-button">
            <ModalDialogButtonV3
              buttonText={constants.COMMENTS_UNRESOLVE_BUTTON_TEXT}
              variant="text"
              header={constants.COMMENTS_UNRESOLVE_DIALOG_HEADER}
              text={constants.COMMENTS_UNRESOLVE_DIALOG_BUTTON_TEXT}
              onSubmit={unresolveComment}
              submitButtonText={constants.COMMENTS_UNRESOLVE_DIALOG_SUBMIT_BUTTON_TEXT}
            />
          </span>
        </span>
      </div>
    );
  }

  const actionButtons = disabled ? null : (
    <span className="display-flex">
      <span className="comment-action-button">
        <ModalDialogButtonV3
          buttonText={constants.COMMENTS_RESOLVED_BUTTON_TEXT}
          variant="text"
          header={constants.COMMENTS_RESOLVED_DIALOG_HEADER}
          text={constants.COMMENTS_RESOLVED_DIALOG_BUTTON_TEXT}
          onSubmit={resolveComment}
          submitButtonText={constants.COMMENTS_RESOLVED_DIALOG_SUBMIT_BUTTON_TEXT}
          visible={!!resolveEnabled}
        />
      </span>
      <span className="comment-action-button">
        <ModalDialogButtonV3
          buttonText={constants.COMMENTS_EDIT_BUTTON_TEXT}
          variant="text"
          header={constants.COMMENTS_EDIT_DIALOG_HEADER}
          onSubmit={updateCommentData}
          inputForm={renderUpdateForm}
          inputValid={!validationErrorMessage}
          submitButtonText={constants.COMMENTS_EDIT_DIALOG_SUBMIT_BUTTON_TEXT}
          visible={isCommentOwner(userComment)}
          // tslint:disable-next-line: jsx-no-lambda
          onCloseModal={() => setUpdatedCommentData(commentData)}
        />
      </span>
      <span className="comment-action-button">
        <ModalDialogButtonV3
          buttonText={constants.COMMENTS_DELETE_BUTTON_TEXT}
          variant="text"
          header={constants.COMMENTS_DELETE_DIALOG_HEADER}
          text={constants.COMMENTS_DELETE_DIALOG_BUTTON_TEXT}
          onSubmit={deleteComment}
          submitButtonText={constants.COMMENTS_DELETE_DIALOG_SUBMIT_BUTTON_TEXT}
          visible={isCommentOwner(userComment)}
        />
      </span>
    </span>
  );

  return (
    <div>
      <div className={`${commentBoxClassName}`}>
        <RenderMultilineText text={commentData} />
      </div>
      {actionButtons}
    </div>
  );
};

export default withAppContext(CommentItem);
